import PropTypes from "prop-types";
import { graphql, useStaticQuery } from "gatsby";

import { useEffect, useMemo } from "react";
import { edgesToArray } from "@helpers/general";

import { withALErrorBoundary } from "@helpers/ErrorBoundary/ALErrorBoundary";

import CustomMTOSvg from "@components/ProductPage/mto/CustomMTOSvg";
import ALShopifyImage from "@components/al_components/ALShopifyImage";

// The engraving overlays are sized in fixed pixels (the nameplate text, the chains framing it, the
// engraved span) against the width the preview has on the product page, which is also the width
// its overlay viewBox is drawn at. The cart thumbnail is a fraction of that, so the whole
// composition (svg + overlays) is rendered at that width and scaled down as one block, keeping the
// engraving where the product page puts it.
const PREVIEW_BASE_WIDTH = 445;

// The thumbnail zooms into the piece the same way the Shopify images next to it are cropped
const PREVIEW_ZOOM = 2.1;

// The desktop thumbnail, the width the per-layout previews below were tuned against
// const PREVIEW_TUNING_WIDTH = 180;

// How each made-to-order layout is framed in the thumbnail. `zoom` multiplies the scale the
// composition is drawn down to, `translate` offsets its center from the container center
// (percentages of the scaled composition, so they hold at both thumbnail widths). Layouts absent
// from here are centered at PREVIEW_ZOOM.
const PREVIEW_LAYOUTS = {
  // The engraving spreads across the whole piece, which the zoom would crop
  initial_necklace: { zoom: 0.8 },
  nameplate: { zoom: 1.3 },
  // Tuned on the desktop thumbnail as scale 0.6 with translate(-70%, -66%) around the element
  // center, which lands the composition center 0.5 / 0.6 - 0.7 and 0.5 / 0.6 - 0.66 of it off
  dome_engraving: {
    // zoom: (0.4 * PREVIEW_BASE_WIDTH) / PREVIEW_TUNING_WIDTH,
    // translate: "13.33%, 17.33%",
    zoom: 1,
  },
  single_engraving: { zoom: 1.3 },
  default: {
    zoom: 1.4,
  },
};

function LineItemImageMto({ lineItem, width, selectedVariantIds, setMtoProducts }) {
  const data = useStaticQuery(graphql`
    query LineItemImageMtoStaticQuery {
      allContentfulProductPageMto {
        edges {
          node {
            id
            product_page {
              handle
            }
            madeToOrderTimelines
            settings {
              internal {
                content
              }
            }
            productSvg {
              file {
                url
                fileName
              }
            }
            variantsPreview {
              title
              file {
                url
                fileName
              }
            }
          }
        }
      }
    }
  `);

  const productMtoDetails = edgesToArray(data.allContentfulProductPageMto);
  const lineItemMtoDetails = productMtoDetails.find((mto) =>
    mto.product_page?.some((pp) => pp.handle === lineItem?.variant?.product?.handle)
  );
  useEffect(() => {
    setMtoProducts(lineItemMtoDetails);
  }, [lineItemMtoDetails, setMtoProducts]);

  // The type of preview to render and the fonts on offer live on the same settings the product
  // page is built from
  const { type, settings } = useMemo(() => {
    try {
      const content = JSON.parse(lineItemMtoDetails?.settings?.internal?.content || "{}");
      return { type: content?.type || "default", settings: content?.settings || {} };
    } catch (e) {
      return { type: "default", settings: {} };
    }
  }, [lineItemMtoDetails]);

  // What the customer engraved, read back from the line item the same way the product page reads
  // it from its settings. Everything about the engraving is on the attributes but the font style,
  // which is recovered from the settings by the font family the line carries.
  const selectedData = useMemo(() => {
    const attributes = lineItem?.attributes || [];
    const getAttribute = (key) => attributes.find((a) => a.key === key)?.value || "";

    // Products offering a font hold the engraving on their font setting, the ones engraving
    // characters without a font choice hold it on the line itself
    const engravingSettingId =
      Object.keys(settings).find((settingId) => settings[settingId]?.type === "font_engraving") ??
      null;
    const suffix = engravingSettingId === null ? "" : `_${engravingSettingId}`;

    // The back engraving is never previewed, only the front is
    const text = getAttribute(`_mto_engraving${suffix}`);
    if (!text) return null;

    const fontFamily = getAttribute(`_mto_font_family${suffix}`);
    const fontName = getAttribute(`_mto_font_name${suffix}`);
    const fonts = settings[engravingSettingId]?.fonts ?? [];
    // Lines added before the font was kept on them fall back on the one the setting engraves with
    const font =
      fonts.find((f) => f.font_family === fontFamily) ??
      (fontFamily ? { name: fontName, font_family: fontFamily } : null) ??
      fonts[0] ??
      null;

    return { font, text };
  }, [lineItem?.attributes, settings]);

  const svgUrl = lineItemMtoDetails?.productSvg?.file?.url;

  // The layout is kept on the line, and recovered from the settings for lines added before it was
  const mtoType = lineItem?.attributes?.find((a) => a.key === "_mto_type")?.value || type;
  const previewLayout = PREVIEW_LAYOUTS[mtoType];
  const previewScale = (width / PREVIEW_BASE_WIDTH) * (previewLayout?.zoom ?? PREVIEW_ZOOM);
  // The composition is centered on the container before it is scaled, so the scale runs around the
  // container center. The offset comes after the scale so it shrinks with the thumbnail: offsetting
  // before it would scale around the composition's own center, a fixed 222px off the container
  // center, and push the piece further out the narrower the thumbnail gets.
  const previewTransform = `translate(-50%, -50%) scale(${previewScale})${
    previewLayout?.translate ? ` translate(${previewLayout.translate})` : ""
  }`;

  if (!data || !lineItemMtoDetails || !selectedVariantIds || !svgUrl) {
    return (
      <ALShopifyImage
        className="aspect-[0.8] scale-[1.4]"
        url={lineItem.variant?.image?.url}
        alt={lineItem.variant?.image?.altText}
        width={width}
      />
    );
  }

  return (
    <div
      className="relative aspect-[0.8] overflow-hidden bg-[#f3f1f2]"
      style={{ width, maxWidth: "100%" }}
    >
      <div
        className="absolute left-1/2 top-1/2"
        style={{
          width: PREVIEW_BASE_WIDTH,
          transform: previewTransform,
        }}
      >
        <CustomMTOSvg
          className="w-full h-auto"
          svgUrl={svgUrl}
          selectedVariantIds={selectedVariantIds}
          selectedData={selectedData}
          type={type}
          variantsPreview={lineItemMtoDetails?.variantsPreview || null}
        />
      </div>
    </div>
  );
}

LineItemImageMto.propTypes = {
  lineItem: PropTypes.object.isRequired,
  width: PropTypes.number,
  selectedVariantIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  setMtoProducts: PropTypes.func.isRequired,
};

export default withALErrorBoundary({
  name: "LineItemImageMto",
  priority: "P1",
})(LineItemImageMto);
